import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { API } from "../constants";

// Cookie-based auth: all axios requests send the httpOnly access_token cookie automatically.
// The token is also kept in React state (memory-only) for legacy component code that passes
// `Authorization: Bearer ${token}` headers explicitly — but localStorage is no longer used,
// closing the XSS attack vector.
axios.defaults.withCredentials = true;

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: hydrate session from the httpOnly cookie via /auth/me
  useEffect(() => {
    let cancelled = false;
    const hydrate = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        if (!cancelled) setUser(response.data);
      } catch (error) {
        // 401 means no cookie / expired — user is anonymous
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    hydrate();
    return () => { cancelled = true; };
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    // Cookie is set by server automatically; we ALSO capture token for legacy Bearer-header callers
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (name, email, password, phone, referrerCode) => {
    const payload = { name, email, password, phone };
    if (referrerCode) payload.referrer_code = referrerCode;
    const response = await axios.post(`${API}/auth/register`, payload);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) { /* still clear locally */ }
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (e) {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
